/**
 * @fileoverview Cart System Constants
 * Centralized configuration and constants for the cart system
 * 
 * 📡 EVENT CONSTANTS:
 * 
 * CART EVENTS:
 * - CART_LOADED   → 'cart:loaded'
 * - CART_UPDATED  → 'cart:updated'
 * - CART_ADDED    → 'cart:added'
 * - CART_REMOVED  → 'cart:removed'
 * - CART_CLEARED  → 'cart:cleared'
 * 
 * OPERATION EVENTS:
 * - OPERATION_STARTED  → 'operation:started'
 * - OPERATION_COMPLETED → 'operation:completed'
 * - OPERATION_FAILED   → 'operation:failed'
 * 
 * UI EVENTS:
 * - DRAWER_OPENED  → 'drawer:opened'
 * - DRAWER_CLOSED  → 'drawer:closed'
 * - DRAWER_UPDATED → 'drawer:updated'
 * - COUNTER_UPDATED → 'counter:updated'
 * 
 * FORM EVENTS:
 * - FORM_SUBMITTED → 'form:submitted'
 * - FORM_SUCCESS   → 'form:success'
 * - FORM_ERROR     → 'form:error'
 * - VARIANT_CHANGED → 'variant:changed'
 * 
 * SYSTEM EVENTS:
 * - SYSTEM_INITIALIZED → 'system:initialized'
 * - SYSTEM_DESTROYED  → 'system:destroyed'
 * - COMPONENT_ADDED   → 'component:added'
 * - COMPONENT_REMOVED → 'component:removed'
 */

export const DEBUG = {
  ENABLED: import.meta.env?.MODE === 'development',
  LOG_PREFIX: '[Cart]',
  ERROR_LOG_PREFIX: '[Cart Error]',
  PERFORMANCE_LOG_PREFIX: '[Cart Performance]',
  WARN_LOG_PREFIX: '[Cart Warning]',
  INFO_LOG_PREFIX: '[Cart Info]',
  DEBUG_LOG_PREFIX: '[Cart Debug]'
};

export const PERFORMANCE_THRESHOLDS = {
  SLOW_LOAD: 1000, // 1 second
  ACCEPTABLE_LOAD: 300, // 300ms
  FAST_LOAD: 100 // 100ms
};

export const API_ENDPOINTS = {
  CART: '/cart.js',
  ADD: '/cart/add.js',
  UPDATE: '/cart/update.js',
  CHANGE: '/cart/change.js',
  CLEAR: '/cart/clear.js'
};

export const ERROR_CODES = {
  CART_ERROR: 'CART_ERROR',
  INVALID_VARIANT: 'INVALID_VARIANT',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  API_ERROR: 'API_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR'
};

export const SELECTORS = {
  // Cart Drawer
  CART_DRAWER: '[data-cart-drawer]',
  CART_OVERLAY: '[data-cart-overlay]',
  CART_CLOSE: '[data-cart-close]',
  CART_TOGGLE: '[data-cart-toggle], [data-cart-drawer-toggle]',
  CART_ITEMS: '[data-drawer-cart-items]',
  
  // Cart Elements
  CART_COUNT: '[data-drawer-cart-count]',
  CART_TOTAL: '[data-drawer-cart-total]',
  CART_SUBTOTAL: '[data-drawer-cart-subtotal]',

  CART_BADGE: '[data-cart-badge]',
  CART_STATE: '[data-cart-state]',
  
  // Cart Actions
  PRODUCT_FORM: '[data-product-form]',
  ADD_TO_CART: '[data-add-to-cart]',
  REMOVE_ITEM: '[data-remove-item]',
  QUANTITY_PLUS: '[data-quantity-plus]',
  QUANTITY_MINUS: '[data-quantity-minus]',
  
  // Mini Cart Elements
  MINI_CART_ITEMS: '[data-mini-cart-items]',
  MINI_CART_COUNT: '[data-mini-cart-count]',
  MINI_CART_TOTAL: '[data-mini-cart-total]',
  MINI_CART_SUBTOTAL: '[data-mini-cart-subtotal]',

};

export const CSS_CLASSES = {
  IS_VISIBLE: 'is-visible',
  IS_HIDDEN: 'is-hidden',
  IS_LOADING: 'is-loading',
  IS_EMPTY: 'is-empty',
  HAS_ITEMS: 'has-items',
  IS_ACTIVE: 'is-active',
  IS_DISABLED: 'is-disabled',
  IS_UPDATING: 'is-updating',
  IS_ADDED: 'is-added',
  IS_REMOVED: 'is-removed',
  IS_ERROR: 'is-error',
  CART_OPEN: 'cart-open'
};

export const CART_EVENTS = {
  // Cart State Events
  LOADED: 'cart:loaded',
  UPDATED: 'cart:updated',
  ADDED: 'cart:added',
  REMOVED: 'cart:removed',
  CLEARED: 'cart:cleared',
  ERROR: 'cart:error',
  
  // UI Events
  DRAWER_OPEN: 'cart:drawer:open',
  DRAWER_CLOSE: 'cart:drawer:close',
  DRAWER_TOGGLE: 'cart:drawer:toggle',
  
  // Debug Events
  DEBUG: 'cart:debug'
};

export const DEFAULT_CONFIG = {
  // API Routes
  moneyFormat: '${{amount}}',
  cartRoute: '/cart.js',
  addRoute: '/cart/add.js',
  updateRoute: '/cart/update.js',
  removeRoute: '/cart/change.js',
  clearRoute: '/cart/clear.js',
  
  // UI Configuration
  autoOpenDrawer: true,
  enableNotifications: true,
  hideWhenEmpty: false,
  animateChanges: true,
  
  // Performance Settings
  debounceDelay: 300,     // Increased from 50ms to 300ms for stability
  batchWindow: 300,       // Increased batch window
  cacheTimeout: 2000,     // 2 seconds cache validity
  optimisticUpdates: true,
  minimumUpdateInterval: 500, // Minimum time between quantity updates
  
  // API Settings - Optimized for slow server responses
  apiTimeout: 15000,       // 15 seconds (increased for slow servers)
  maxRetries: 2,          // Reduced retries to fail faster
  retryDelay: 3000,       // 3 seconds between retries
  
  // UI Settings
  notificationDuration: 3000,
  
  // Debug Settings
  debug: false,
  logLevel: 'info'
}; 